"use client"
import { useState, useEffect } from "react"
import Rotate from "@/components/Rotate/Rotate"
import Hero from "@/components/Hero/Hero"
import Alliance from "@/components/Home/Alliance"
import Industries from "@/components/Home/Industries"
import Soc from "@/components/Home/Soc"
import SwiperSection from "@/components/Home/Swipersection"
import Whysection from "@/components/Home/Whysection"
import Marquee from "@/components/Marquee/Marquee"
import Certificates from "@/components/Home/Certificates"

function Main() {
  // SSR: loader markup is always present on initial render
  const [loading, setLoading] = useState(true)
  const [progress, setProgress] = useState(0)
  const [loadedCount, setLoadedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  // Helper to get all images in main content
  useEffect(() => {
    // Wait for DOM to be ready
    if (typeof window === "undefined") return

    // Dynamically import Lenis to keep it out of the critical bundle
    let rafId: number
    import("lenis").then(({ default: Lenis }) => {
      const lenis = new Lenis({
        duration: 2.5,
        lerp: 0.08,
      })
      function raf(time: number) {
        lenis.raf(time)
        rafId = requestAnimationFrame(raf)
      }
      rafId = requestAnimationFrame(raf)

      // Pause rAF when page enters bfcache to allow back/forward navigation
      const onPageHide = () => cancelAnimationFrame(rafId)
      const onPageShow = () => {
        rafId = requestAnimationFrame(raf)
      }
      window.addEventListener("pagehide", onPageHide)
      window.addEventListener("pageshow", onPageShow)

      return () => {
        cancelAnimationFrame(rafId)
        lenis.destroy()
        window.removeEventListener("pagehide", onPageHide)
        window.removeEventListener("pageshow", onPageShow)
      }
    })

    // Find all images in main content
    const mainImages = document.querySelectorAll("main img")
    setTotalCount(mainImages.length)
    let loaded = 0
    mainImages.forEach((img) => {
      const image = img as HTMLImageElement
      if (image.complete) {
        loaded++
      } else {
        image.addEventListener(
          "load",
          () => {
            setLoadedCount((prev) => prev + 1)
          },
          { once: true },
        )
        image.addEventListener(
          "error",
          () => {
            setLoadedCount((prev) => prev + 1)
          },
          { once: true },
        )
      }
    })
    setLoadedCount(loaded)
  }, [])

  // Progress bar updates as images/components load
  useEffect(() => {
    if (totalCount === 0) {
      setProgress(100)
      setLoading(false)
      return
    }
    const percent = Math.round((loadedCount / totalCount) * 100)
    setProgress(percent)
    if (loadedCount >= totalCount && totalCount > 0) {
      setTimeout(() => {
        setLoading(false)
      }, 300) // slight delay for smoothness
    }
  }, [loadedCount, totalCount])

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Rotate />
        <p className="font-oswald text-[32px] mt-6 font-semibold">
          {progress}%
        </p>
      </div>
    )
  }
  return (
    <main>
      <Hero />
      <Marquee />
      <SwiperSection />
      <Alliance />
      <Soc />
      <Certificates />
      <Whysection />
      <Industries />
    </main>
  )
}

export default Main
